import { useEffect, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import { liveStreamAPI } from '../../api/liveStreamApis/liveStream.api';

type LiveExpertsMap = Record<string, boolean>;
type StatusSubscriber = (status: LiveExpertsMap) => void;

// One shared status store for every ExpertCard using this hook.
let liveExperts: LiveExpertsMap = {};
let statusSocket: Socket | null = null;
let activeSubscribers = new Set<StatusSubscriber>();
let connectionGeneration = 0;

const SOCKET_URL =
  import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

function notifySubscribers(): void {
  activeSubscribers.forEach((subscriber) => {
    subscriber(liveExperts);
  });
}

function updateExpertStatus(
  expertCode: string,
  isLive: boolean,
): void {
  if (!expertCode) {
    return;
  }

  liveExperts = {
    ...liveExperts,
    [expertCode]: isLive,
  };

  notifySubscribers();
}

function startSharedResources(): void {
  if (statusSocket || activeSubscribers.size === 0) {
    return;
  }

  const generation = ++connectionGeneration;

  // One socket for all mounted expert cards.
  const socket = io(SOCKET_URL, {
    transports: ['websocket'],
  });

  statusSocket = socket;

  socket.on(
    'expert_live_status',
    ({
      expertCode,
      isLive,
    }: {
      expertCode: string;
      isLive: boolean;
    }) => {
      if (
        generation !== connectionGeneration ||
        statusSocket !== socket
      ) {
        return;
      }

      updateExpertStatus(expertCode, isLive);
    },
  );

  // One initial API request for the shared store.
  void (async () => {
    try {
      const data = await liveStreamAPI.getActiveStreams('All');
      const streams = data?.streams || data || [];

      // Ignore results from an obsolete connection lifecycle.
      if (
        generation !== connectionGeneration ||
        statusSocket !== socket ||
        activeSubscribers.size === 0
      ) {
        return;
      }

      const activeMap: LiveExpertsMap = {};

      streams.forEach((stream: { expertCode?: string }) => {
        if (stream.expertCode) {
          activeMap[stream.expertCode] = true;
        }
      });

      // Preserve existing merge behavior.
      liveExperts = {
        ...liveExperts,
        ...activeMap,
      };

      notifySubscribers();
    } catch (error) {
      console.error(
        'Failed to fetch initial live status from DB:',
        error,
      );
    }
  })();
}

function stopSharedResources(): void {
  // Invalidate any pending API response from the old lifecycle.
  connectionGeneration += 1;

  if (statusSocket) {
    statusSocket.disconnect();
    statusSocket = null;
  }

  liveExperts = {};
}

function subscribeToLiveStatus(
  subscriber: StatusSubscriber,
): () => void {
  activeSubscribers.add(subscriber);

  // Give the new subscriber the current shared state immediately.
  subscriber(liveExperts);

  if (activeSubscribers.size === 1) {
    startSharedResources();
  }

  let unsubscribed = false;

  return () => {
    if (unsubscribed) {
      return;
    }

    unsubscribed = true;
    activeSubscribers.delete(subscriber);

    // Disconnect only when no cards still need live status.
    if (activeSubscribers.size === 0) {
      stopSharedResources();
    }
  };
}

export function useLiveStreamStatus(): LiveExpertsMap {
  const [liveExpertsMap, setLiveExpertsMap] =
    useState<LiveExpertsMap>(() => liveExperts);

  useEffect(() => {
    return subscribeToLiveStatus(setLiveExpertsMap);
  }, []);

  return liveExpertsMap;
}